import type { GetStaticPaths, GetStaticProps, GetServerSideProps } from "next";
import { ApiService } from "services/server";
import { Tab } from "views";
import {
  normalizeTabs,
  normalizeTabsWithDetails,
  slugMaker,
} from "utils/normalizer";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { REVALIDATE_TIME, TAG_TREES } from "utils/constants";
import { checkEmpty, is404, isCorrectLocale } from "utils/newFunction";
import { Formatter } from "utils/formatter";

function TabsPage({ data }) {
  const router = useRouter();

  const [tabs, setTabs] = useState(() => data?.tabs || []);
  // const [breadcrumb, setBreadcrumb] = useState(() => data?.breadcrumb || []);

  const [activeTab, breadcrumb] = useMemo(() => {
    const activeTabKey =
      typeof router.query.slug === "string"
        ? router.query.slug
        : router.query.slug?.[1];
    const active = tabs.find((item) => item?.slug === activeTabKey);
    if (active) {
      return [active, data?.breadcrumb?.concat({ title: active.tabName })];
    }
    return [tabs[0], data?.breadcrumb];
  }, [router.query.slug, tabs]);

  useEffect(() => {
    setTabs(data?.tabs || []);
  }, [data?.tabs]);

  const onTabChange = (slug: string) => {
    router.push(`/tab/${router?.query?.slug?.[0]}/${slug}`, undefined, {
      shallow: true,
    });
  };

  return (
    <Tab
      data={data}
      onTabChange={onTabChange}
      activeTab={activeTab}
      breadcrumb={breadcrumb}
    />
  );
}

// slug[0] => general content entityId
// slug[1] => tab content slug

// export const getStaticPaths: GetStaticPaths = async () => {
//   return {
//     paths: [],
//     fallback: true,
//   };
// };

// export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
//   const apiService = new ApiService(locale);
//   const entityId = params?.slug?.[0] || "";

//   const requests = [
//     apiService.getContentByEntityId(+entityId), // 0
//   ];

//   const response = await ApiService.multipleRequest(requests);

//   if (checkEmpty(...response)) {
//     throw new Error();
//   }

//   const general = response?.[0]?.[0];
//   const tabsResult = normalizeTabsWithDetails(normalizeTabs(general?.tabs));

//   return {
//     props: {
//       data: {
//         breadcrumb: general?.breadcrumb || [],
//         tabs: tabsResult,
//         metaTag: general?.metaTag || {},
//         changeLanguageLink: general?.changeLanguageLink?.value || "/",
//       },
//     },
//     revalidate: REVALIDATE_TIME,
//   };
// };

export const getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
}) => {
  const apiService = new ApiService(locale);
  const entityId = params?.slug?.[0] || "";

  if (Number.isNaN(+entityId)) {
    return {
      notFound: true,
    };
  }

  try {
    const requests = [
      apiService.getGeneralById(+entityId, { formatter: false }), // 0
    ];
    let response = [];
    try {
      response = await ApiService.multipleRequest(requests);
    } catch (e) {}

    if (is404(response?.[0])) {
      return {
        notFound: true,
      };
    }

    const general = Formatter.formatData({ data: response?.[0] })?.[0];

    if (!isCorrectLocale(general, locale)) {
      return {
        notFound: true,
      };
    }
    if (general && !("PageTab" in (general?.tabs?.[0] || {}))) {
      return {
        notFound: true,
      };
    }
    const tabsResult = normalizeTabsWithDetails(normalizeTabs(general?.tabs));
    const destination = `${
      locale === "en" ? "/en" : ""
    }/tab/${entityId}/${encodeURIComponent(tabsResult[0]?.slug)}`;
    const isExist = tabsResult.find((item) => item?.slug === params?.slug?.[1]);
    if (!isExist) {
      return {
        redirect: {
          destination,
        },
      };
    }

    return {
      props: {
        data: {
          breadcrumb: general?.breadcrumb || [],
          tabs: tabsResult,
          metaTag: general?.metaTag || {},
          changeLanguageLink: general?.changeLanguageLink?.value || "/",
        },
      },
    };
  } catch (error) {
    console.log("error", error);
    return {
      redirect: {
        destination: "/500",
        permanent: false,
      },
    };
  }
};

export default TabsPage;
